import { notification } from 'antd'
import i18next from 'i18next'
import { action, makeAutoObservable, observable } from 'mobx'

import { FileEntityType, fileService } from 'services/FileService'
import { eLockApi } from 'sections/LKSection/modules/ELockModule/api'
import {
  ElockInfoStatus,
  ELockPhoto,
  ElockReason,
  OrderELockInfoDTO,
} from 'sections/LKSection/modules/ELockModule/types'
import { OrderKeys } from 'sections/LKSection/modules/OrderModule/data'
import { ConvertedFile } from 'utils/helpers/files/fileUpload'
import { getSeparatedFiles } from 'utils/helpers/files/getSeparatedFiles'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'

const getKey = (key: string) => `${OrderKeys.eLockModule}.api.${key}`

export class ElockReasonModel {
  @observable eLockReasonValue: ElockReason | null = null
  @observable description = ''
  @observable activateWithoutELockPhotos: ConvertedFile[] = []
  @observable status: ElockInfoStatus = ElockInfoStatus.none

  constructor() {
    makeAutoObservable(this)
  }

  initialize = (value: OrderELockInfoDTO) => {
    if (value.reason !== ElockReason.none) {
      this.setELockReasonValue(value.reason)
    }
    this.setDescription(value.description)
    this.setActivateWithoutELockPhotos(
      value.photos
        .filter((photo) => photo.type === ELockPhoto.reason)
        .map((photo) => ({ content: photo.url, fileSize: 0, format: '', name: '' })),
    )
    this.setStatus(value.status)
  }

  @action
  setELockReasonValue = (value: ElockReason | null) => {
    this.eLockReasonValue = value
  }

  @action
  setDescription = (value: string) => {
    this.description = value
  }

  @action
  setActivateWithoutELockPhotos = (value: ConvertedFile[]) => {
    this.activateWithoutELockPhotos = value
  }

  @action
  setStatus = (value: ElockInfoStatus) => {
    this.status = value
  }

  reset = () => {
    this.setELockReasonValue(null)
    this.setDescription('')
    this.setActivateWithoutELockPhotos([])
  }

  async setELockReason(orderId: number) {
    const photos = await this.filesUpload(this.activateWithoutELockPhotos, orderId)

    if (photos) {
      try {
        await eLockApi.setELockReason({
          orderId,
          reason: this.eLockReasonValue ?? ElockReason.other,
          description: this.description,
          photos,
        })

        notification.success({ message: i18next.t(getSharedKey('api.createOrderSuccess')) })

        return true
      } catch (error) {
        notification.error({
          message: i18next.t(getSharedKey('api.createOrderError')),
          description: (error as Error).message,
        })
      }
    }
  }

  async updateELockReason(orderId: number) {
    const photos = await this.filesUpload(this.activateWithoutELockPhotos, orderId)

    if (photos) {
      try {
        await eLockApi.updateELock({
          orderId,
          photos,
          description: this.description,
          reason: this.eLockReasonValue ?? ElockReason.other,
        })

        return true
      } catch (error) {
        notification.error({
          message: i18next.t(getKey('updateELockReasonError')),
          description: (error as Error).message,
        })
      }
    }
  }

  async filesUpload(photos: ConvertedFile[], orderId: number) {
    const { newFiles: newPhotos, oldFiles: oldPhotos } = getSeparatedFiles(photos)

    try {
      const uploadedPhotos = await fileService.filesUpload({
        entityId: orderId,
        entityType: FileEntityType.pickPointOrder,
        files: newPhotos.map((photo) => ({ content: photo.content, totalSize: photo.fileSize })),
      })

      if (uploadedPhotos.length < newPhotos.length) {
        notification.error({
          message: i18next.t(getSharedKey('api.filesUploadError')),
        })

        return false
      }

      return [
        ...uploadedPhotos.map((photo) => ({ url: photo.imageUrl, type: ELockPhoto.reason })),
        ...oldPhotos.map((photo) => ({ url: photo.content, type: ELockPhoto.reason })),
      ]
    } catch (error) {
      notification.error({
        message: i18next.t(getSharedKey('api.photosUploadError')),
        description: (error as Error).message,
      })

      return false
    }
  }
}
